import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Button
} from '@mui/material';
import DOMPurify from 'dompurify';
import { config } from '../config';
import { getDerivativeImageUrl, getImageUrl } from '../utils/imageUrl';

function NewsArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Process images in content
  const processContentImages = (content) => {
    if (!content) return '';
    return content.replace(
      /<img[^>]+src="([^"]+)"/g,
      (match, src) => {
        const newSrc = getDerivativeImageUrl(src, 'medium');
        const originalSrc = getImageUrl(src);
        let updated = match.replace(src, newSrc);
        if (!/data-original-src=/i.test(updated)) updated = updated.replace('<img', `<img data-original-src="${originalSrc}"`);
        if (!/loading=/i.test(updated)) updated = updated.replace('<img', '<img loading="lazy"');
        if (!/decoding=/i.test(updated)) updated = updated.replace('<img', '<img decoding="async"');
        return updated;
      }
    );
  };

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await fetch(`${config.API_URL}/api/news/${id}`);
        if (!response.ok) {
          throw new Error('Article not found');
        }
        const data = await response.json();
        
        // Process the article content
        const processedArticle = {
          ...data,
          content: processContentImages(data.content)
        };
        
        setArticle(processedArticle);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching article:', error);
        setError('Failed to load article. Please try again later.');
        setLoading(false);
      }
    };

    if (id) {
      fetchArticle();
    }
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!article) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Article not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Button 
          onClick={() => navigate('/news')} 
          sx={{ mb: 2 }}
          variant="outlined"
        >
          Back to News
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
          {article.title}
        </Typography>
        
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {new Date(article.createdAt).toLocaleDateString()}
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ 
          mt: 2,
          '& a, & a:visited': {
            color: 'primary.main',
            textDecorationColor: 'rgba(245, 151, 79, 0.55)'
          },
          '& a:hover, & a:focus': {
            color: 'primary.light',
            textDecorationColor: 'primary.light'
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            display: 'block',
            margin: '1em auto',
            borderRadius: '4px'
          }
        }}>
          <div 
            dangerouslySetInnerHTML={{ 
              __html: DOMPurify.sanitize(article.content)
            }} 
          />
        </Box>
      </Paper>
    </Container>
  );
}

export default NewsArticle;
